
import { AddVatTuItemsFor } from "@/core/enums/addVatTuItemsFor.enum";
import {
  getErrorMsg,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { useAccount } from "@/core/hooks/use-account";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import {
  IVatTuResponseBase,
  IVatTuSearchInKhoResponseBase,
} from "@/core/interfaces/ApiResponses";
import PhieuXuatKhoService from "@/core/services/Receipt.service";
import { VatTuService } from "@/core/services/VatTu.service";
import { ElInput } from "element-plus";
import { differenceWith } from "lodash";
import { defineComponent, nextTick, PropType, ref } from "vue";

import DanhMucVatTuService from "../../core/services/DanhMucVatTu.service";

import { KhoService } from "@/core/services/Kho.service";
import ButtonCustom from "../shared/buttons/ButtonCustom.vue";
import { FormModalShell } from "@/components/shared/modal";
import QuantityInputCustom from "../forms-elements/QuantityInputCustom.vue";
import AddVatTuTableItems from "./add-vat-tu-table-items/AddVatTuTableItems.vue";

export default defineComponent({
  name: "add-vat-tu-items-v2",

  components: {
    AddVatTuTableItems,
    ButtonCustom,
    FormModalShell,
    QuantityInputCustom,
  },

  emits: ["close", "added"],

  setup() {
    const { currentUserData } = useAccount();
    const { currentPage, totalItems, perPage, perPageArr } = usePagination();
    const { isLoading, startLoading, endLoading } = useLoading();
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();
    const vatTuInput = ref<null | typeof ElInput>(null);
    const danhMucVatTuInput = ref<null | typeof ElInput>(null);
    const soLuongInput = ref<null | typeof ElInput>(null);

    return {
      vatTuInput,
      danhMucVatTuInput,
      soLuongInput,
      currentUserData,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      isLoading,
      startLoading,
      endLoading,
      ButtonTypeColors,
      ButtonsType,
    };
  },

  props: {
    isModalOpen: {
      type: Boolean,
      required: true,
    },
    khoId: {
      type: Number,
      default: 0,
    },
    showSearchDmvt: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    useFor: {
      type: String as PropType<AddVatTuItemsFor>,
      default: AddVatTuItemsFor.KHO,
    },
    vatTuSelected: {
      type: Array as PropType<Array<IVatTuResponseBase>>,
      default: () => [],
    },
  },

  data() {
    return {
      isOpen: false,
      totalVatTuItems: 0,
      displayingVatTuItems: [] as IVatTuResponseBase[],
      selectingVatTuItems: [] as IVatTuResponseBase[],
      isSelected: false,
      danhMucVatTuId: 0,
      vatTuId: 0,
      tenDanhMucVatTu: "",
      tenVatTu: "",
      isDanhMucItemsAvailable: true,
      isVatTuItemsAvailable: true,
      so_luong: 1,
      soLuongLimit: 0,
      don_gia: 1000,
      hasQuantityError: false,
      quantityErrorMessage: "",
      vatTuAutocompleteKey: 0,
    };
  },

  watch: {
    async isModalOpen(value) {
      this.isOpen = value;

      if (value) {
        this.hasQuantityError = false;
        this.quantityErrorMessage = "";

        // After FormModalShell/BaseModal mounts and its auto-focus timer (~80ms).
        await nextTick();
        window.setTimeout(() => {
          this.focusVatTuSearch(true);
        }, 100);
      }
    },

    tenVatTu(value) {
      if (!value) {
        this.isVatTuItemsAvailable = true;
      }
    },

    // Watch vatTuId changes to update limit quantity
    vatTuId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          // Reset limit quantity when vatTuId changes
          this.soLuongLimit = 0;

          // Clear validation errors when changing selection
          this.hasQuantityError = false;
          this.quantityErrorMessage = "";

          // Reset quantity to 1 when changing selection
          this.so_luong = 1;
        }
      },
      immediate: true,
    },

    // Watch so_luong changes for real-time validation
    so_luong: {
      handler(newVal) {
        if (newVal !== undefined && newVal !== null) {
          this.validateQuantity();
        }
      },
      immediate: false,
    },
  },

  computed: {
    AddVatTuItemsFor() {
      return AddVatTuItemsFor;
    },
    isDonGiaInputDisabled() {
      return this.vatTuId === 0 || this.so_luong === 0;
    },
    titleDisplay() {
      if (this.useFor === AddVatTuItemsFor.ADD_VAT_TU_DINH_MUC_CO_DINH) {
        return "Thêm vật tư vào định mức cố định";
      }

      if (this.useFor === AddVatTuItemsFor.DINH_MUC) {
        return "Thêm vật tư vào định mức";
      }

      return "Thêm vật tư";
    },
    isAddButtonDisable() {
      return (
        this.vatTuId === 0 ||
        this.so_luong <= 0 ||
        isNaN(this.so_luong) ||
        this.hasQuantityError
      );
    },
    emptyText() {
      return "Chưa có vật tư nào được chọn";
    },
    isPhieuXuatKhoUseFor() {
      return [
        AddVatTuItemsFor.PHIEU_XUAT_KHO,
        AddVatTuItemsFor.PHIEU_XUAT_KHO_VAT_TU_THU_HOI,
      ].includes(this.useFor);
    },
    triggerSearchOnFocus() {
      return this.isPhieuXuatKhoUseFor;
    },
    quantityInputMax() {
      if (!this.isPhieuXuatKhoUseFor || this.soLuongLimit <= 0) {
        return Number.POSITIVE_INFINITY;
      }

      return +this.soLuongLimit;
    },
  },

  methods: {
    focusVatTuSearch(triggerFetch = false) {
      const autocomplete = this.vatTuInput as {
        focus?: () => void;
        getInput?: () => HTMLInputElement | null;
      } | null;

      autocomplete?.focus?.();
      autocomplete?.getInput?.()?.select();

      if (triggerFetch && this.triggerSearchOnFocus) {
        this.triggerVatTuSearch(this.tenVatTu || "");
      }
    },

    triggerVatTuSearch(queryString = "") {
      this.queryVatTu(queryString, (suggestions) => {
        const autocomplete = this.vatTuInput as {
          suggestions?: IVatTuResponseBase[];
          highlightedIndex?: number;
          activated?: boolean;
        } | null;

        if (!autocomplete) {
          return;
        }

        autocomplete.suggestions = suggestions;
        autocomplete.highlightedIndex = suggestions.length ? 0 : -1;
        autocomplete.activated = suggestions.length > 0;
        this.isVatTuItemsAvailable = suggestions.length > 0 || !queryString;
      });
    },

    onVatTuSearchFocus(event: FocusEvent) {
      (event.target as HTMLInputElement)?.select();

      if (this.triggerSearchOnFocus && !this.tenVatTu) {
        this.triggerVatTuSearch("");
      }
    },

    clearSelectingVatTu() {
      this.isSelected = false;

      this.tenVatTu = "";

      this.vatTuId = 0;

      this.soLuongLimit = 0;

      this.hasQuantityError = false;
      this.quantityErrorMessage = "";
      this.vatTuAutocompleteKey += 1;

      nextTick(() => {
        this.focusVatTuSearch(true);
      });
    },

    validateQuantity() {
      // Reset error state
      this.hasQuantityError = false;
      this.quantityErrorMessage = "";

      // Check if quantity is valid
      if (!this.so_luong || this.so_luong <= 0 || isNaN(this.so_luong)) {
        this.hasQuantityError = true;
        this.quantityErrorMessage = "Số lượng phải lớn hơn 0";

        return false;
      }

      // Check if quantity is a valid number
      if (typeof this.so_luong !== "number" || !isFinite(this.so_luong)) {
        this.hasQuantityError = true;
        this.quantityErrorMessage = "Số lượng phải là số hợp lệ";

        return false;
      }

      // Check against soLuongLimit for PHIEU_NHAP_KHO_KHAC
      if (
        [
          AddVatTuItemsFor.PHIEU_XUAT_KHO,
          AddVatTuItemsFor.PHIEU_XUAT_KHO_VAT_TU_THU_HOI,
        ].includes(this.useFor) &&
        this.soLuongLimit > 0
      ) {
        if (this.so_luong > this.soLuongLimit) {
          this.hasQuantityError = true;
          this.quantityErrorMessage = `Số lượng không được vượt quá ${this.soLuongLimit}`;

          return false;
        }
      }

      // Additional validation for decimal places (max 2 decimal places)
      const decimalPlaces = (this.so_luong.toString().split(".")[1] || "")
        .length;
      if (decimalPlaces > 2) {
        this.hasQuantityError = true;
        this.quantityErrorMessage =
          "Số lượng chỉ được tối đa 2 chữ số thập phân";
        return false;
      }
      return true;
    },

    onRemoveItem(id: number) {
      this.selectingVatTuItems = this.selectingVatTuItems.filter(
        (item) => item.id !== id
      );
    },

    onClose() {
      this.isOpen = false;

      this.$emit("close");
    },

    vatTuItemsTransform(
      items: Array<IVatTuResponseBase>
    ): Array<IVatTuResponseBase> {
      if (this.useFor === AddVatTuItemsFor.KHO) {
        return items.map((vatTu) => ({
          ...vatTu,
          don_gia: vatTu[`don_gia_kho_${this.khoId}`],
          so_luong: vatTu[`so_luong_kho_${this.khoId}`],
        }));
      }

      return [];
    },

    async queryDanhMucVatTu(queryString: string, fn) {
      const {
        data: {
          data: { data },
        },
      } = await DanhMucVatTuService.searchByMaDanhMuc(queryString);

      if (data.length === 0 && queryString) {
        this.isDanhMucItemsAvailable = false;
      } else if (data.length >= 0) {
        this.isDanhMucItemsAvailable = true;
      }

      fn(data);
    },

    async queryVatTu(queryString: string, fn) {
      let {
        data: { data: result },
      } = await VatTuService.getSearch({
        term: queryString,
        id_dm_vat_tu: this.danhMucVatTuId,
        ...(this.useFor === AddVatTuItemsFor.KHO && {
          id_kho_exclude: this.khoId,
        }),
        ...(this.useFor === AddVatTuItemsFor.PHIEU_XUAT_KHO && {
          id_kho: this.khoId,
        }),
        ...(this.useFor === AddVatTuItemsFor.PHIEU_XUAT_KHO_VAT_TU_THU_HOI && {
          id_kho: this.khoId,
          loai_vat_tu: 1, // vât tư thu hồi
        }),
      });

      result = differenceWith(
        result,
        this.vatTuSelected,
        (newItem, selectedItem: IVatTuResponseBase) => {
          return newItem.id === selectedItem.id;
        }
      );

      result = differenceWith(
        result,
        this.selectingVatTuItems,
        (newItem, selectedItem: IVatTuResponseBase) =>
          newItem.id === selectedItem.id
      );

      fn(result);
    },

    onSelectDanhMucVatTu({ id }) {
      this.danhMucVatTuId = id;

      if (this.vatTuInput) {
        this.vatTuInput.focus();
      }
    },

    async onSelectVatTu(
      vatTu: IVatTuResponseBase | IVatTuSearchInKhoResponseBase
    ) {
      const { id } = vatTu;

      this.isSelected =
        this.selectingVatTuItems.findIndex((item) => item.id === id) >= 0;

      if (this.isSelected) {
        return;
      }

      this.vatTuId = id;

      // Reset limit quantity first
      this.soLuongLimit = 0;

      // Clear validation errors when selecting new item
      this.hasQuantityError = false;
      this.quantityErrorMessage = "";

      if (
        [
          AddVatTuItemsFor.PHIEU_XUAT_KHO,
          AddVatTuItemsFor.PHIEU_XUAT_KHO_VAT_TU_THU_HOI,
        ].includes(this.useFor)
      ) {
        // Call API to get ton kho information
        try {
          await this.startLoading();

          const {
            data: { data },
          } = await PhieuXuatKhoService.getTonKho(id, this.khoId);

          await this.endLoading();

          // Extract so_luong from API response and set to soLuongLimit
          this.soLuongLimit = +data.so_luong;
          this.don_gia = +data.don_gia;

          if (this.soLuongInput?.focus) {
            this.soLuongInput.focus();
          }
        } catch (error) {
          await this.endLoading();

          await swalErrNotification(
            error,
            "Có lỗi xảy ra khi lấy thông tin tồn kho"
          );

          // Reset selection on error
          this.vatTuId = 0;
          this.tenVatTu = "";
          this.soLuongLimit = 0;
          this.don_gia = 1000;
        }
      } else if (this.soLuongInput?.focus) {
        this.soLuongInput.focus();
      }
    },

    async onAddVatTu() {
      // Validate quantity before adding
      if (!this.validateQuantity()) {
        return;
      }

      // Get VatTu details for adding to table
      try {
        this.isLoading = true;

        const {
          data: { data: res },
        } = await VatTuService.fetchSupplyDetail(this.vatTuId);

        await nextTick();

        if (res) {
          this.selectingVatTuItems.push({
            ...res,
            so_luong: this.so_luong,
            ...(this.isPhieuXuatKhoUseFor && {
              so_luong_ton: this.soLuongLimit,
              don_gia: +this.don_gia,
              pivot: {
                don_gia: +this.don_gia,
              },
            }),
          });

          this.focusVatTuSearch(false);

          this.tenVatTu = "";

          this.vatTuId = 0;

          this.so_luong = 1;

          this.don_gia = 1000;

          this.danhMucVatTuId = 0;

          this.soLuongLimit = 0;

          // Clear validation errors after successful add
          this.hasQuantityError = false;
          this.quantityErrorMessage = "";
        }
      } catch (error) {
        await swalErrNotification(
          error,
          "Có lỗi xảy ra khi lấy thông tin vật tư"
        );
      } finally {
        this.isLoading = false;
      }
    },

    async submit() {
      this.danhMucVatTuId = 0;

      this.vatTuId = 0;

      this.tenDanhMucVatTu = "";

      this.so_luong = 1;

      this.don_gia = 1000;

      if (
        [
          AddVatTuItemsFor.DINH_MUC,
          AddVatTuItemsFor.PHIEU_XUAT_KHO,
          AddVatTuItemsFor.PHIEU_XUAT_KHO_VAT_TU_THU_HOI,
          AddVatTuItemsFor.ADD_VAT_TU_DINH_MUC_CO_DINH,
        ].includes(this.useFor)
      ) {
        this.$emit("added", {
          selectingVatTuItems: this.selectingVatTuItems,
        });

        this.displayingVatTuItems = [];

        this.selectingVatTuItems = [];

        this.isOpen = false;

        return;
      }

      try {
        await KhoService.assignVatTuItemVaoKho({
          id_kho: this.khoId,
          vat_tu_items: this.selectingVatTuItems.map(({ id, so_luong }) => ({
            id,
            so_luong,
          })),
        });

        this.$emit("added");
      } catch (e) {
        this.isOpen = false;

        await swalNotification(
          getErrorMsg(e, "có lỗi khi gán vật tư vào kho này"),
          "error"
        );

        this.$emit("added");
      }
    },
  },
});
